
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import Index from "@/pages/Index";
import Profile from "@/pages/Profile";
import UserProfile from "@/pages/UserProfile";
import Settings from "@/pages/Settings";
import AirportDetails from "@/pages/AirportDetails";
import NewReview from "@/pages/NewReview";
import ReviewDetails from "@/pages/ReviewDetails";
import Admin from "@/pages/Admin";
import AdminUsers from "@/pages/AdminUsers";
import AdminAirports from "@/pages/AdminAirports";
import AdminReviews from "@/pages/AdminReviews";
import AdminSecurityEvents from "@/pages/AdminSecurityEvents";
import AdminSecurityEventDetail from "@/pages/AdminSecurityEventDetail";
import SearchResults from "@/pages/SearchResults";
import Notifications from "@/pages/Notifications";
import NotFound from "@/pages/NotFound";
import AdminFeedback from "@/pages/AdminFeedback";
import AdminReports from "@/pages/AdminReports";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import About from "@/pages/About";
import Resources from "@/pages/Resources";
import Contact from "@/pages/Contact";
import Inventory from "@/pages/Inventory";
import FilmDetails from "@/pages/FilmDetails";
import CameraDetails from "@/pages/CameraDetails";
import LensDetails from "@/pages/LensDetails";
import Auth from "@/pages/Auth";
import { AuthCallback } from "@/components/auth/AuthCallback";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { RootRouteHandler } from "@/components/auth/RootRouteHandler";
import Regions from "@/pages/Regions";
import RegionDetail from "@/pages/RegionDetail";
import Countries from "@/pages/Countries";
import CountryDetail from "@/pages/CountryDetail";
import Develop from "@/pages/Develop";
import { FeatureProtectedRoute } from "@/components/router/FeatureProtectedRoute";
import { useDarkroomMode } from "@/hooks/useDarkroomMode";
import { useEffect, useCallback } from "react";
import { Capacitor } from '@capacitor/core';

// Protected route with darkroom check
const DarkroomProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { canNavigateAway } = useDarkroomMode();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if running in Capacitor (native app)
  const isCapacitorApp = Capacitor.isNativePlatform();
  
  useEffect(() => {
    // When this component mounts, check if navigation is allowed
    if (location.pathname !== '/develop' && !canNavigateAway(location.pathname)) {
      // Force navigate back to develop if in darkroom mode
      navigate('/develop');
    }
    
    // Log navigation in app
    if (isCapacitorApp) {
      console.log('In-app navigation to:', location.pathname);
    }
  }, [location.pathname, navigate, canNavigateAway, isCapacitorApp]);
  
  return <>{children}</>;
};

export const AppRoutes = () => {
  // Use a useEffect to log when routes change (useful for debugging)
  const location = useLocation();
  
  useEffect(() => {
    console.log('Route changed to:', location.pathname);
  }, [location.pathname]);
  
  return (
    <Routes>
      <Route path="/" element={
        <DarkroomProtectedRoute>
          <RootRouteHandler />
        </DarkroomProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><Index /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/callback" element={<AuthCallback />} />
      <Route path="/auth" element={
        <DarkroomProtectedRoute>
          <Auth />
        </DarkroomProtectedRoute>
      } />
      <Route path="/profile" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><Profile /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/user/:id" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><UserProfile /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/settings" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><Settings /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/airport/:code" element={
        <DarkroomProtectedRoute>
          <AirportDetails />
        </DarkroomProtectedRoute>
      } />
      <Route path="/review/new" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><NewReview /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/new-review" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><NewReview /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/review/:id" element={
        <DarkroomProtectedRoute>
          <ReviewDetails />
        </DarkroomProtectedRoute>
      } />
      <Route path="/resources" element={
        <DarkroomProtectedRoute>
          <Resources />
        </DarkroomProtectedRoute>
      } />
      <Route path="/about" element={
        <DarkroomProtectedRoute>
          <About />
        </DarkroomProtectedRoute>
      } />
      <Route path="/contact" element={
        <DarkroomProtectedRoute>
          <Contact />
        </DarkroomProtectedRoute>
      } />
      <Route path="/develop" element={<ProtectedRoute><Develop /></ProtectedRoute>} />
      <Route path="/admin" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><Admin /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><AdminUsers /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/admin/airports" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><AdminAirports /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/admin/reviews" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><AdminReviews /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/admin/security" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><AdminSecurityEvents /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/admin/security/:id" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><AdminSecurityEventDetail /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/search" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><SearchResults /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/notifications" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><Notifications /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/admin/feedback" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><AdminFeedback /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><AdminReports /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/privacy" element={
        <DarkroomProtectedRoute>
          <Privacy />
        </DarkroomProtectedRoute>
      } />
      <Route path="/terms" element={
        <DarkroomProtectedRoute>
          <Terms />
        </DarkroomProtectedRoute>
      } />
      <Route path="/inventory" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><Inventory /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/inventory/:id" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><FilmDetails /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/camera/:id" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><CameraDetails /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/lens/:id" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><LensDetails /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/inventory/camera/edit/:id" element={
        <DarkroomProtectedRoute>
          <ProtectedRoute><Inventory /></ProtectedRoute>
        </DarkroomProtectedRoute>
      } />
      <Route path="/regions" element={
        <DarkroomProtectedRoute>
          <Regions />
        </DarkroomProtectedRoute>
      } />
      <Route path="/regions/:regionId" element={
        <DarkroomProtectedRoute>
          <RegionDetail />
        </DarkroomProtectedRoute>
      } />
      <Route path="/countries" element={
        <DarkroomProtectedRoute>
          <Countries />
        </DarkroomProtectedRoute>
      } />
      <Route path="/countries/:countryId" element={
        <DarkroomProtectedRoute>
          <CountryDetail />
        </DarkroomProtectedRoute>
      } />
      <Route path="*" element={
        <DarkroomProtectedRoute>
          <NotFound />
        </DarkroomProtectedRoute>
      } />
    </Routes>
  );
};
